import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  CreditCard,
  Download,
  KeyRound,
  ListChecks,
  Lock,
  LogOut,
  RefreshCcw,
  Settings,
  SquareKanban,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const STORAGE_KEY = 'admin_services_password';

const toDateKey = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return format(parsed, 'yyyy-MM-dd');
};

const getAppointmentDateKey = (appointment) => {
  const preferredDate = String(appointment?.preferredDate || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(preferredDate)) {
    return preferredDate;
  }

  return toDateKey(appointment?.submittedAt);
};

const getReadableDateTime = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString();
};

const normalizeOffDays = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const unique = [];
  values.forEach((item) => {
    const normalized = String(item || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized) && !unique.includes(normalized)) {
      unique.push(normalized);
    }
  });

  return unique.sort();
};

const normalizePreferredHour = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2})(?::\d{1,2})?$/);
  if (!match) {
    return '';
  }

  const hour = Number(match[1]);
  if (Number.isNaN(hour) || hour < 0 || hour > 23) {
    return '';
  }

  return `${String(hour).padStart(2, '0')}:00`;
};

const formatMoney = (amount, currency = 'EUR') => {
  const numeric = Number(amount || 0);
  const safeCurrency = String(currency || 'EUR')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3) || 'EUR';

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch (error) {
    return `${numeric.toFixed(2)} ${safeCurrency}`;
  }
};

const BOOKING_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'pending_manual', label: 'Pending Manual' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const BOOKINGS_PAGE_SIZE = 10;

const invoiceNumberForAppointment = (appointment) => {
  const parsed = new Date(appointment?.submittedAt);
  const datePart = Number.isNaN(parsed.getTime())
    ? '00000000'
    : `${parsed.getFullYear()}${String(parsed.getMonth() + 1).padStart(2, '0')}${String(
        parsed.getDate(),
      ).padStart(2, '0')}`;
  const suffix = String(appointment?.id || '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(-6) || '000000';
  return `RE-${datePart}-${suffix}`;
};

const AdminHome = () => {
  const [activeTab, setActiveTab] = useState('appointments');
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const [servicesCount, setServicesCount] = useState(0);
  const [appointmentTypesCount, setAppointmentTypesCount] = useState(0);
  const [appointmentTypeLabels, setAppointmentTypeLabels] = useState({});

  const [settingsSummary, setSettingsSummary] = useState({
    businessName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [paymentSummary, setPaymentSummary] = useState({
    enableManualPayments: false,
    enablePaypalPayments: false,
    paypalEnvironment: 'sandbox',
    paypalCurrency: 'EUR',
    onlinePaymentMinimumPercent: 50,
    bookingBufferMinutes: 60,
  });
  const [siteSettingsPayload, setSiteSettingsPayload] = useState(null);
  const [offDays, setOffDays] = useState([]);
  const [isSavingOffDay, setIsSavingOffDay] = useState(false);

  const [contentSummary, setContentSummary] = useState({
    specialties: 0,
    testimonials: 0,
    coreValues: 0,
    faqs: 0,
    resources: 0,
  });

  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isUpdatingAppointmentId, setIsUpdatingAppointmentId] = useState('');
  const [isDownloadingInvoiceId, setIsDownloadingInvoiceId] = useState('');
  const [isDeletingAppointmentId, setIsDeletingAppointmentId] = useState('');
  const [bookingsSearchQuery, setBookingsSearchQuery] = useState('');
  const [bookingsStatusFilter, setBookingsStatusFilter] = useState('all');
  const [bookingsPaymentFilter, setBookingsPaymentFilter] = useState('all');
  const [bookingsServiceFilter, setBookingsServiceFilter] = useState('all');
  const [bookingsPage, setBookingsPage] = useState(1);

  const verifyPassword = useCallback(async (password, silent = false) => {
    if (!password) {
      if (!silent) {
        setStatus({ type: 'error', text: 'Enter your admin password.' });
      }
      setIsCheckingAuth(false);
      setIsAuthenticated(false);
      return false;
    }

    setIsVerifying(true);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`Auth failed (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, password);
      setAdminPassword(password);
      setIsAuthenticated(true);
      setStatus({ type: '', text: '' });
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      if (!silent) {
        setStatus({ type: 'error', text: 'Invalid admin password.' });
      }
      return false;
    } finally {
      setIsVerifying(false);
      setIsCheckingAuth(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setIsLoadingSummary(true);

    try {
      const adminHeaders = adminPassword
        ? {
            'X-Admin-Password': adminPassword,
          }
        : {};

      const [servicesResponse, settingsResponse, appointmentsResponse, sectionsResponse] = await Promise.all([
        fetch('/api/content/services', {
          method: 'GET',
          headers: { Accept: 'application/json' },
        }),
        fetch('/api/content/site-settings', {
          method: 'GET',
          headers: { Accept: 'application/json' },
        }),
        fetch('/api/appointments', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...adminHeaders,
          },
        }),
        fetch('/api/content/sections', {
          method: 'GET',
          headers: { Accept: 'application/json' },
        }),
      ]);

      if (!servicesResponse.ok || !settingsResponse.ok || !appointmentsResponse.ok || !sectionsResponse.ok) {
        throw new Error('Failed to load admin summary content');
      }

      const servicesPayload = await servicesResponse.json();
      const settingsPayload = await settingsResponse.json();
      const appointmentsPayload = await appointmentsResponse.json();
      const sectionsPayload = await sectionsResponse.json();

      const services = Array.isArray(servicesPayload?.services)
        ? servicesPayload.services
        : [];
      const appointmentTypes = Array.isArray(servicesPayload?.appointmentTypes)
        ? servicesPayload.appointmentTypes
        : [];
      const appointmentItems = Array.isArray(appointmentsPayload) ? appointmentsPayload : [];

      setServicesCount(services.length);
      setAppointmentTypesCount(appointmentTypes.length);
      setAppointmentTypeLabels(
        appointmentTypes.reduce((accumulator, item) => {
          if (item?.value) {
            accumulator[item.value] = item.label || item.value;
          }
          return accumulator;
        }, {}),
      );

      setSettingsSummary({
        businessName: String(settingsPayload?.businessName || ''),
        contactEmail: String(settingsPayload?.contactEmail || ''),
        contactPhone: String(settingsPayload?.contactPhone || ''),
      });
      setPaymentSummary({
        enableManualPayments: Boolean(settingsPayload?.enableManualPayments),
        enablePaypalPayments: Boolean(settingsPayload?.enablePaypalPayments),
        paypalEnvironment:
          String(settingsPayload?.paypalEnvironment || 'sandbox').trim().toLowerCase() === 'live'
            ? 'live'
            : 'sandbox',
        paypalCurrency:
          String(settingsPayload?.paypalCurrency || 'EUR')
            .replace(/[^A-Za-z]/g, '')
            .toUpperCase()
            .slice(0, 3) || 'EUR',
        onlinePaymentMinimumPercent: Math.min(
          100,
          Math.max(50, Number(settingsPayload?.onlinePaymentMinimumPercent) || 50),
        ),
        bookingBufferMinutes: Math.max(0, Math.round(Number(settingsPayload?.bookingBufferMinutes) || 0)),
      });
      setSiteSettingsPayload(settingsPayload && typeof settingsPayload === 'object' ? settingsPayload : null);
      setOffDays(normalizeOffDays(settingsPayload?.offDays));

      setContentSummary({
        specialties: Array.isArray(sectionsPayload?.specialties) ? sectionsPayload.specialties.length : 0,
        testimonials: Array.isArray(sectionsPayload?.testimonials) ? sectionsPayload.testimonials.length : 0,
        coreValues: Array.isArray(sectionsPayload?.coreValues) ? sectionsPayload.coreValues.length : 0,
        faqs: Array.isArray(sectionsPayload?.faqs) ? sectionsPayload.faqs.length : 0,
        resources: Array.isArray(sectionsPayload?.resources) ? sectionsPayload.resources.length : 0,
      });

      setAppointments(appointmentItems);
      setStatus({ type: '', text: '' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to load admin summary. Please reload and try again.',
      });
    } finally {
      setIsLoadingSummary(false);
    }
  }, [adminPassword]);

  const saveOffDays = useCallback(
    async (nextOffDays) => {
      if (!siteSettingsPayload || !adminPassword) {
        setStatus({
          type: 'error',
          text: 'Site settings are not loaded yet. Reload and try again.',
        });
        return;
      }

      const normalizedOffDays = normalizeOffDays(nextOffDays);
      setIsSavingOffDay(true);
      setStatus({ type: '', text: '' });

      try {
        const response = await fetch('/api/content/site-settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Admin-Password': adminPassword,
          },
          body: JSON.stringify({
            ...siteSettingsPayload,
            offDays: normalizedOffDays,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            sessionStorage.removeItem(STORAGE_KEY);
            setIsAuthenticated(false);
            setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
            return;
          }
          throw new Error(`Failed to save off days (${response.status})`);
        }

        const updated = await response.json();
        const persistedOffDays = normalizeOffDays(updated?.offDays);
        setSiteSettingsPayload(updated);
        setOffDays(persistedOffDays);
        setStatus({ type: 'success', text: 'Off days updated.' });
      } catch (error) {
        console.error(error);
        setStatus({
          type: 'error',
          text: error?.message || 'Failed to save off days.',
        });
      } finally {
        setIsSavingOffDay(false);
      }
    },
    [adminPassword, siteSettingsPayload],
  );

  useEffect(() => {
    const storedPassword = sessionStorage.getItem(STORAGE_KEY);

    if (!storedPassword) {
      setIsCheckingAuth(false);
      return;
    }

    setAdminPassword(storedPassword);
    verifyPassword(storedPassword, true);
  }, [verifyPassword]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSummary();
    }
  }, [isAuthenticated, loadSummary]);

  const handleSignIn = async (event) => {
    event.preventDefault();
    await verifyPassword(adminPassword, false);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setAdminPassword("");
    setSiteSettingsPayload(null);
    setOffDays([]);
    setStatus({ type: '', text: '' });
  };

  const updateAppointmentStatus = async (appointmentId, nextStatus, nextPaymentStatus) => {
    if (!appointmentId) {
      return;
    }

    setIsUpdatingAppointmentId(appointmentId);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch(`/api/appointments/${encodeURIComponent(appointmentId)}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify({
          status: nextStatus,
          paymentStatus: nextPaymentStatus,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }

        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || `Failed to update booking (${response.status})`);
      }

      const updated = await response.json();
      setAppointments((current) =>
        current.map((item) => (item.id === appointmentId ? updated : item)),
      );
      setStatus({ type: 'success', text: 'Booking status updated.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: error?.message || 'Failed to update booking status.',
      });
    } finally {
      setIsUpdatingAppointmentId('');
    }
  };

  const downloadInvoice = async (appointmentId) => {
    if (!appointmentId) {
      return;
    }

    setIsDownloadingInvoiceId(appointmentId);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch(
        `/api/appointments/${encodeURIComponent(appointmentId)}/invoice.pdf`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
            'X-Admin-Password': adminPassword,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }
        throw new Error(`Failed to download invoice (${response.status})`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
      const asciiMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
      const serverFilename = utf8Match
        ? decodeURIComponent(utf8Match[1])
        : asciiMatch
          ? asciiMatch[1]
          : '';
      anchor.href = url;
      anchor.download = serverFilename || `invoice-${appointmentId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      setStatus({ type: 'success', text: 'Invoice PDF downloaded.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: error?.message || 'Failed to download invoice PDF.',
      });
    } finally {
      setIsDownloadingInvoiceId('');
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!appointmentId) {
      return;
    }

    const confirmed = window.confirm(
      'Delete this booking and its invoice record? This action cannot be undone.',
    );
    if (!confirmed) {
      return;
    }

    setIsDeletingAppointmentId(appointmentId);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch(`/api/appointments/${encodeURIComponent(appointmentId)}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || `Failed to delete booking (${response.status})`);
      }

      setAppointments((current) => current.filter((item) => item.id !== appointmentId));
      setStatus({ type: 'success', text: 'Booking deleted.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: error?.message || 'Failed to delete booking.',
      });
    } finally {
      setIsDeletingAppointmentId('');
    }
  };

  const appointmentDays = useMemo(() => {
    const uniqueKeys = Array.from(
      new Set(
        appointments
          .map(getAppointmentDateKey)
          .filter(Boolean),
      ),
    );

    return uniqueKeys
      .map((key) => new Date(`${key}T00:00:00`))
      .filter((dateValue) => !Number.isNaN(dateValue.getTime()));
  }, [appointments]);
  const offDayDates = useMemo(() => {
    return offDays
      .map((key) => new Date(`${key}T00:00:00`))
      .filter((dateValue) => !Number.isNaN(dateValue.getTime()));
  }, [offDays]);
  const offDaysSet = useMemo(() => new Set(offDays), [offDays]);

  const selectedDateKey = useMemo(() => toDateKey(selectedDate), [selectedDate]);
  const isSelectedDateOffDay = useMemo(
    () => Boolean(selectedDateKey && offDaysSet.has(selectedDateKey)),
    [offDaysSet, selectedDateKey],
  );

  const selectedAppointments = useMemo(
    () =>
      appointments.filter((item) => {
        return getAppointmentDateKey(item) === selectedDateKey;
      }),
    [appointments, selectedDateKey],
  );

  const bookingStatusSummary = useMemo(() => {
    return appointments.reduce(
      (accumulator, item) => {
        const normalized = String(item?.status || 'pending').toLowerCase();
        if (!accumulator[normalized]) {
          accumulator[normalized] = 0;
        }
        accumulator[normalized] += 1;
        return accumulator;
      },
      {
        pending: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0,
        no_show: 0,
      },
    );
  }, [appointments]);

  const bookingServiceOptions = useMemo(() => {
    const byValue = new Map();
    appointments.forEach((appointment) => {
      const value = String(appointment?.serviceType || '').trim();
      if (!value) {
        return;
      }
      const label = appointmentTypeLabels[value] || value;
      if (!byValue.has(value)) {
        byValue.set(value, label);
      }
    });

    return Array.from(byValue.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }));
  }, [appointments, appointmentTypeLabels]);

  const normalizedBookingsQuery = useMemo(
    () => String(bookingsSearchQuery || '').trim().toLowerCase(),
    [bookingsSearchQuery],
  );

  const filteredBookings = useMemo(() => {
    return appointments.filter((appointment) => {
      const statusValue = String(appointment?.status || 'pending').toLowerCase();
      const paymentStatusValue = String(appointment?.paymentStatus || 'pending').toLowerCase();
      const serviceValue = String(appointment?.serviceType || '').trim();

      if (bookingsStatusFilter !== 'all' && statusValue !== bookingsStatusFilter) {
        return false;
      }
      if (bookingsPaymentFilter !== 'all' && paymentStatusValue !== bookingsPaymentFilter) {
        return false;
      }
      if (bookingsServiceFilter !== 'all' && serviceValue !== bookingsServiceFilter) {
        return false;
      }

      if (!normalizedBookingsQuery) {
        return true;
      }

      const serviceLabel = appointmentTypeLabels[serviceValue] || serviceValue;
      const invoiceNumber = invoiceNumberForAppointment(appointment);
      const preferredHour = normalizePreferredHour(appointment?.preferredTime);
      const searchable = [
        appointment?.id,
        invoiceNumber,
        appointment?.name,
        appointment?.email,
        appointment?.phone,
        serviceLabel,
        appointment?.preferredDate,
        preferredHour,
        statusValue,
        paymentStatusValue,
        appointment?.paymentMethod,
        appointment?.message,
      ]
        .map((item) => String(item || '').toLowerCase())
        .join(' ');

      return searchable.includes(normalizedBookingsQuery);
    });
  }, [
    appointments,
    appointmentTypeLabels,
    bookingsPaymentFilter,
    bookingsServiceFilter,
    bookingsStatusFilter,
    normalizedBookingsQuery,
  ]);

  useEffect(() => {
    setBookingsPage(1);
  }, [bookingsSearchQuery, bookingsStatusFilter, bookingsPaymentFilter, bookingsServiceFilter]);

  const totalBookingPages = useMemo(
    () => Math.max(1, Math.ceil(filteredBookings.length / BOOKINGS_PAGE_SIZE)),
    [filteredBookings.length],
  );

  useEffect(() => {
    if (bookingsPage > totalBookingPages) {
      setBookingsPage(totalBookingPages);
    }
  }, [bookingsPage, totalBookingPages]);

  const paginatedBookings = useMemo(() => {
    const start = (bookingsPage - 1) * BOOKINGS_PAGE_SIZE;
    return filteredBookings.slice(start, start + BOOKINGS_PAGE_SIZE);
  }, [bookingsPage, filteredBookings]);

  const bookingRange = useMemo(() => {
    if (filteredBookings.length === 0) {
      return { start: 0, end: 0 };
    }
    const start = (bookingsPage - 1) * BOOKINGS_PAGE_SIZE + 1;
    const end = Math.min(filteredBookings.length, start + BOOKINGS_PAGE_SIZE - 1);
    return { start, end };
  }, [bookingsPage, filteredBookings.length]);

  const toggleSelectedDateOffDay = async () => {
    if (!selectedDateKey) {
      return;
    }

    const shouldRemove = offDaysSet.has(selectedDateKey);
    const nextOffDays = shouldRemove
      ? offDays.filter((item) => item !== selectedDateKey)
      : [...offDays, selectedDateKey];
    await saveOffDays(nextOffDays);
  };

  const removeOffDay = async (dayKey) => {
    const normalizedDayKey = String(dayKey || '').trim();
    if (!normalizedDayKey) {
      return;
    }

    await saveOffDays(offDays.filter((item) => item !== normalizedDayKey));
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200">
            <CardContent className="p-6 text-sage-700">Checking admin session...</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage-900">
                <Lock className="w-5 h-5" />
                Admin Login
              </CardTitle>
              <CardDescription>
                Sign in to manage your website content.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-sage-700">Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    className="mt-1.5 border-sage-300"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-700"
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Sign In'}
                </Button>
                {status.text && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {status.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage-50 py-10">
      <div className="container mx-auto px-4 max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-sage-900">Admin Dashboard</h1>
            <p className="text-sage-600 mt-1">Manage appointments and website content.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={loadSummary}
              disabled={isLoadingSummary}
              className="border-sage-300"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Reload
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              className="border-sage-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {status.text && (
          <div
            className={`rounded-lg px-4 py-3 text-sm font-medium ${
              status.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}
          >
            {status.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[240px,minmax(0,1fr)] gap-6">
          <Card className="border-sage-200 h-fit">
            <CardHeader>
              <CardTitle className="text-sage-900 text-lg">Sections</CardTitle>
              <CardDescription>Use tabs to open each area.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <button
                type="button"
                onClick={() => setActiveTab('appointments')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'appointments'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <CalendarDays className="w-4 h-4" />
                  Appointments
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('services')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'services'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <SquareKanban className="w-4 h-4" />
                  Services
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('bookings')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'bookings'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <ListChecks className="w-4 h-4" />
                  Booking List
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('settings')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'settings'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <Settings className="w-4 h-4" />
                  Settings
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('payments')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'payments'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <CreditCard className="w-4 h-4" />
                  Payments
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('content')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'content'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <Sparkles className="w-4 h-4" />
                  Website Content
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('password')}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  activeTab === 'password'
                    ? 'bg-sage-100 border-sage-300 text-sage-900'
                    : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                }`}
              >
                <span className="inline-flex items-center gap-2 font-medium">
                  <KeyRound className="w-4 h-4" />
                  Password
                </span>
              </button>
            </CardContent>
          </Card>

          {activeTab === 'appointments' && (
            <Card className="border-sage-200 shadow-sm min-w-0">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Appointment Calendar</CardTitle>
                <CardDescription>
                  Bookings are saved from the public booking form and shown by date.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-sm">
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Pending</p>
                    <p className="text-xl font-semibold text-sage-900">{bookingStatusSummary.pending}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Confirmed</p>
                    <p className="text-xl font-semibold text-sage-900">{bookingStatusSummary.confirmed}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Completed</p>
                    <p className="text-xl font-semibold text-sage-900">{bookingStatusSummary.completed}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Cancelled</p>
                    <p className="text-xl font-semibold text-sage-900">{bookingStatusSummary.cancelled}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">No Show</p>
                    <p className="text-xl font-semibold text-sage-900">{bookingStatusSummary.no_show}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[320px,minmax(0,1fr)] gap-6">
                  <div className="rounded-lg border border-sage-200 p-3">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(nextDate) => {
                        if (nextDate) {
                          setSelectedDate(nextDate);
                        }
                      }}
                      modifiers={{ hasAppointments: appointmentDays, offDay: offDayDates }}
                      modifiersClassNames={{
                        hasAppointments:
                          'bg-sage-100 text-sage-900 font-semibold border border-sage-300',
                        offDay:
                          'bg-red-100 text-red-800 font-semibold border border-red-300',
                      }}
                      className="rounded-md"
                    />
                    <div className="mt-4 space-y-3 rounded-md border border-sage-200 bg-sage-50 p-3 text-sm">
                      <p className="font-medium text-sage-900">Off Day Controls</p>
                      <p className="text-sage-700">
                        Selected date: <strong>{selectedDateKey || '-'}</strong>
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className={
                          isSelectedDateOffDay
                            ? 'w-full border-red-300 text-red-700 hover:bg-red-50'
                            : 'w-full border-sage-300'
                        }
                        disabled={!selectedDateKey || isSavingOffDay}
                        onClick={toggleSelectedDateOffDay}
                      >
                        {isSavingOffDay
                          ? 'Saving...'
                          : isSelectedDateOffDay
                            ? 'Unblock Selected Day'
                            : 'Block Selected Day'}
                      </Button>
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-wide text-sage-500">
                          Blocked Days ({offDays.length})
                        </p>
                        {offDays.length === 0 ? (
                          <p className="text-sage-600">No blocked days configured.</p>
                        ) : (
                          offDays.map((dayKey) => (
                            <div
                              key={`off-day-${dayKey}`}
                              className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-2 py-1"
                            >
                              <span className="text-red-800">{dayKey}</span>
                              <button
                                type="button"
                                className="text-xs font-medium text-red-700 hover:text-red-900"
                                onClick={() => removeOffDay(dayKey)}
                                disabled={isSavingOffDay}
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 min-w-0">
                    <h3 className="font-semibold text-sage-900">
                      Appointments for {selectedDateKey || '-'} ({selectedAppointments.length})
                    </h3>
                    {isSelectedDateOffDay && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                        This date is currently blocked as an off day.
                      </div>
                    )}

                    {selectedAppointments.length === 0 && (
                      <div className="rounded-lg border border-sage-200 bg-sage-50 px-4 py-3 text-sm text-sage-700">
                        No appointments on this day.
                      </div>
                    )}

                    {selectedAppointments.map((appointment) => {
                      const serviceLabel =
                        appointmentTypeLabels[appointment.serviceType] || appointment.serviceType;
                      return (
                        <div
                          key={appointment.id}
                          className="rounded-lg border border-sage-200 bg-sage-50 px-4 py-3 text-sm text-sage-800 space-y-2"
                        >
                          <p>
                            <strong>{appointment.name}</strong> ({appointment.status})
                          </p>
                          <p><strong>Service:</strong> {serviceLabel}</p>
                          <p><strong>Email:</strong> {appointment.email}</p>
                          <p><strong>Phone:</strong> {appointment.phone}</p>
                          <p>
                            <strong>Preferred:</strong> {appointment.preferredDate || '-'}{' '}
                            {normalizePreferredHour(appointment.preferredTime) || ''}
                          </p>
                          <p><strong>Consultation:</strong> {appointment.consultationType || '-'}</p>
                          <p><strong>Payment Method:</strong> {appointment.paymentMethod || '-'}</p>
                          <p><strong>Payment Status:</strong> {appointment.paymentStatus || '-'}</p>
                          <p>
                            <strong>Paid Now:</strong>{' '}
                            {appointment.paymentAmount || appointment.paymentAmount === 0
                              ? formatMoney(appointment.paymentAmount, appointment.paymentCurrency)
                              : '-'}
                          </p>
                          <p>
                            <strong>Total:</strong>{' '}
                            {appointment.totalAmount || appointment.totalAmount === 0
                              ? formatMoney(appointment.totalAmount, appointment.paymentCurrency)
                              : '-'}
                          </p>
                          <p>
                            <strong>Outstanding:</strong>{' '}
                            {appointment.outstandingAmount || appointment.outstandingAmount === 0
                              ? formatMoney(appointment.outstandingAmount, appointment.paymentCurrency)
                              : '-'}
                          </p>
                          <p><strong>Submitted:</strong> {getReadableDateTime(appointment.submittedAt)}</p>
                          {appointment.message && (
                            <p><strong>Message:</strong> {appointment.message}</p>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                            <div>
                              <Label className="text-xs text-sage-700">Booking Status</Label>
                              <select
                                value={String(appointment.status || 'pending')}
                                onChange={(event) =>
                                  updateAppointmentStatus(
                                    appointment.id,
                                    event.target.value,
                                    String(appointment.paymentStatus || 'pending'),
                                  )
                                }
                                disabled={isUpdatingAppointmentId === appointment.id}
                                className="mt-1 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                              >
                                {BOOKING_STATUS_OPTIONS.map((item) => (
                                  <option key={item.value} value={item.value}>
                                    {item.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <Label className="text-xs text-sage-700">Payment Status</Label>
                              <select
                                value={String(appointment.paymentStatus || 'pending')}
                                onChange={(event) =>
                                  updateAppointmentStatus(
                                    appointment.id,
                                    String(appointment.status || 'pending'),
                                    event.target.value,
                                  )
                                }
                                disabled={isUpdatingAppointmentId === appointment.id}
                                className="mt-1 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                              >
                                {PAYMENT_STATUS_OPTIONS.map((item) => (
                                  <option key={item.value} value={item.value}>
                                    {item.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="pt-1 flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="border-sage-300"
                              disabled={
                                isDownloadingInvoiceId === appointment.id ||
                                isDeletingAppointmentId === appointment.id
                              }
                              onClick={() => downloadInvoice(appointment.id)}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              {isDownloadingInvoiceId === appointment.id
                                ? 'Preparing PDF...'
                                : 'Download Invoice PDF'}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              disabled={isDeletingAppointmentId === appointment.id}
                              onClick={() => deleteAppointment(appointment.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {isDeletingAppointmentId === appointment.id ? 'Deleting...' : 'Delete Booking'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

              </CardContent>
            </Card>
          )}

          {activeTab === 'bookings' && (
            <Card className="border-sage-200 shadow-sm min-w-0">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Booking List</CardTitle>
                <CardDescription>
                  Full list of all booking requests with filters, pagination, invoice download, and delete.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-5 gap-3">
                  <div className="2xl:col-span-2 min-w-0">
                    <Label className="text-xs text-sage-700">Search</Label>
                    <Input
                      value={bookingsSearchQuery}
                      onChange={(event) => setBookingsSearchQuery(event.target.value)}
                      placeholder="Search by client, email, invoice number, service..."
                      className="mt-1 border-sage-300"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-sage-700">Booking Status</Label>
                    <select
                      value={bookingsStatusFilter}
                      onChange={(event) => setBookingsStatusFilter(event.target.value)}
                      className="mt-1 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="all">All</option>
                      {BOOKING_STATUS_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-sage-700">Payment Status</Label>
                    <select
                      value={bookingsPaymentFilter}
                      onChange={(event) => setBookingsPaymentFilter(event.target.value)}
                      className="mt-1 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="all">All</option>
                      {PAYMENT_STATUS_OPTIONS.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-sage-700">Service</Label>
                    <select
                      value={bookingsServiceFilter}
                      onChange={(event) => setBookingsServiceFilter(event.target.value)}
                      className="mt-1 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="all">All</option>
                      {bookingServiceOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-sage-700">
                    {filteredBookings.length > 0
                      ? `Showing ${bookingRange.start}-${bookingRange.end} of ${filteredBookings.length} bookings`
                      : 'No bookings match the current filters.'}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-sage-300"
                    onClick={() => {
                      setBookingsSearchQuery('');
                      setBookingsStatusFilter('all');
                      setBookingsPaymentFilter('all');
                      setBookingsServiceFilter('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>

                <div className="w-full max-w-full rounded-lg border border-sage-200 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-sage-100 text-sage-800">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">Submitted</th>
                        <th className="text-left font-medium px-3 py-2">Client</th>
                        <th className="text-left font-medium px-3 py-2">Service</th>
                        <th className="text-left font-medium px-3 py-2">Preferred</th>
                        <th className="text-left font-medium px-3 py-2">Status</th>
                        <th className="text-left font-medium px-3 py-2">Payment</th>
                        <th className="text-left font-medium px-3 py-2">Invoice No.</th>
                        <th className="text-left font-medium px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedBookings.length === 0 && (
                        <tr className="border-t border-sage-200">
                          <td className="px-3 py-6 text-center text-sage-600" colSpan={8}>
                            No bookings found.
                          </td>
                        </tr>
                      )}
                      {paginatedBookings.map((appointment) => {
                        const serviceLabel =
                          appointmentTypeLabels[appointment.serviceType] || appointment.serviceType;
                        const invoiceNumber = invoiceNumberForAppointment(appointment);
                        return (
                          <tr key={`row-${appointment.id}`} className="border-t border-sage-200">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {getReadableDateTime(appointment.submittedAt)}
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-sage-900">{appointment.name}</div>
                              <div className="text-xs text-sage-600">{appointment.email}</div>
                            </td>
                            <td className="px-3 py-2">{serviceLabel}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {(appointment.preferredDate || '-')}{' '}
                              {normalizePreferredHour(appointment.preferredTime) || ''}
                            </td>
                            <td className="px-3 py-2">
                              {String(appointment.status || 'pending').replace('_', ' ')}
                            </td>
                            <td className="px-3 py-2">
                              <div>{String(appointment.paymentStatus || 'pending').replace('_', ' ')}</div>
                              <div className="text-xs text-sage-600">
                                {appointment.totalAmount || appointment.totalAmount === 0
                                  ? formatMoney(appointment.totalAmount, appointment.paymentCurrency)
                                  : '-'}
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{invoiceNumber}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-sage-300"
                                  disabled={
                                    isDownloadingInvoiceId === appointment.id ||
                                    isDeletingAppointmentId === appointment.id
                                  }
                                  onClick={() => downloadInvoice(appointment.id)}
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  PDF
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-red-300 text-red-700 hover:bg-red-50"
                                  disabled={isDeletingAppointmentId === appointment.id}
                                  onClick={() => deleteAppointment(appointment.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  {isDeletingAppointmentId === appointment.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-sage-700">
                    Page {bookingsPage} of {totalBookingPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-sage-300"
                      onClick={() => setBookingsPage((current) => Math.max(1, current - 1))}
                      disabled={bookingsPage <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-sage-300"
                      onClick={() => setBookingsPage((current) => Math.min(totalBookingPages, current + 1))}
                      disabled={bookingsPage >= totalBookingPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'services' && (
            <Card className="border-sage-200 shadow-sm">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Services</CardTitle>
                <CardDescription>
                  Edit service cards and booking service options.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-sage-500">Services</p>
                    <p className="text-2xl font-semibold text-sage-900 mt-1">{servicesCount}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-sage-500">Booking Types</p>
                    <p className="text-2xl font-semibold text-sage-900 mt-1">{appointmentTypesCount}</p>
                  </div>
                </div>
                <Link to="/admin/services">
                  <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                    Open Services Editor
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {activeTab === 'settings' && (
            <Card className="border-sage-200 shadow-sm">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Settings</CardTitle>
                <CardDescription>
                  Edit global business and contact information used across the website.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-3">
                <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 text-sm text-sage-800">
                  <p><strong>Business:</strong> {settingsSummary.businessName || '-'}</p>
                  <p><strong>Email:</strong> {settingsSummary.contactEmail || '-'}</p>
                  <p><strong>Phone:</strong> {settingsSummary.contactPhone || '-'}</p>
                </div>
                <Link to="/admin/settings">
                  <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                    Open Settings Editor
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {activeTab === 'payments' && (
            <Card className="border-sage-200 shadow-sm">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Payments</CardTitle>
                <CardDescription>
                  Configure payment methods, taxes, booking fee, cancellation terms, and booking buffer.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-3">
                <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 text-sm text-sage-800 space-y-1">
                  <p>
                    <strong>Manual Payments:</strong>{' '}
                    {paymentSummary.enableManualPayments ? 'Enabled' : 'Disabled'}
                  </p>
                  <p>
                    <strong>PayPal:</strong> {paymentSummary.enablePaypalPayments ? 'Enabled' : 'Disabled'}
                  </p>
                  <p>
                    <strong>PayPal Mode:</strong> {paymentSummary.paypalEnvironment}
                  </p>
                  <p>
                    <strong>Currency:</strong> {paymentSummary.paypalCurrency}
                  </p>
                  <p>
                    <strong>Minimum Online Payment:</strong>{' '}
                    {Number(paymentSummary.onlinePaymentMinimumPercent || 50).toFixed(0)}%
                  </p>
                  <p>
                    <strong>Post-Booking Buffer:</strong> {paymentSummary.bookingBufferMinutes} minutes
                  </p>
                </div>
                <Link to="/admin/payments">
                  <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                    Open Payments Editor
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {activeTab === 'content' && (
            <Card className="border-sage-200 shadow-sm">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Website Content</CardTitle>
                <CardDescription>
                  Manage about section, expertise, testimonials, core values, FAQs, and resources.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center text-sm">
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Expertise</p>
                    <p className="text-xl font-semibold text-sage-900">{contentSummary.specialties}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Experiences</p>
                    <p className="text-xl font-semibold text-sage-900">{contentSummary.testimonials}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Core Values</p>
                    <p className="text-xl font-semibold text-sage-900">{contentSummary.coreValues}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">FAQs</p>
                    <p className="text-xl font-semibold text-sage-900">{contentSummary.faqs}</p>
                  </div>
                  <div className="rounded-lg border border-sage-200 bg-sage-50 p-3">
                    <p className="text-sage-500">Resources</p>
                    <p className="text-xl font-semibold text-sage-900">{contentSummary.resources}</p>
                  </div>
                </div>
                <Link to="/admin/content">
                  <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                    Open Content Editor
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {activeTab === 'password' && (
            <Card className="border-sage-200 shadow-sm">
              <CardHeader className="bg-white border-b border-sage-200">
                <CardTitle className="text-sage-900">Password Security</CardTitle>
                <CardDescription>
                  Change or reset the admin login password.
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white pt-6 space-y-3">
                <div className="rounded-lg border border-sage-200 bg-sage-50 p-4 text-sm text-sage-800">
                  <p>
                    Use a strong password with at least 8 characters. After updating, your current
                    session will continue with the new password.
                  </p>
                </div>
                <Link to="/admin/password">
                  <Button className="bg-sage-600 hover:bg-sage-700 text-white">
                    Open Password Manager
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
